// components/FormFields/CustomInput/CustomAutocompleteInput.jsx
import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  TextField,
  Paper,
  List,
  ListItem,
  ListItemText,
  ClickAwayListener,
  CircularProgress,
  Typography,
  alpha,
} from "@mui/material";

const CustomAutocompleteInput = ({
  label,
  value,
  onChange,
  onSelect,
  suggestions = [],
  loading = false,
  placeholder = "Type to search...",
  error = false,
  helperText = "",
  disabled = false,
  fullWidth = true,
  size = "small",
  minChars = 2,
  maxResults = 10,
  noOptionsText = "No results found",
  loadingText = "Loading...",
  filterKey = "name",
  displayKey = "name",
  valueKey = "id",
  onSearchChange,
  ...props
}) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value || "");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  const filteredSuggestions = suggestions
    .filter((item) => {
      const searchValue = inputValue.toLowerCase();
      const itemValue = String(item[filterKey] || item[displayKey] || item).toLowerCase();
      return itemValue.includes(searchValue);
    })
    .slice(0, maxResults);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setOpen(newValue.length >= minChars);
    setHighlightedIndex(-1);

    if (onSearchChange) {
      onSearchChange(newValue);
    }

    if (onChange) {
      onChange({
        target: {
          name: props.name,
          value: newValue,
        },
      });
    }
  };

  const handleSelect = (item) => {
    const displayValue = item[displayKey] || item[filterKey] || item;
    setInputValue(displayValue);
    setOpen(false);
    setHighlightedIndex(-1);

    if (onSelect) {
      onSelect(item);
    }

    if (onChange) {
      onChange({
        target: {
          name: props.name,
          value: displayValue,
        },
      });
    }
  };

  const handleKeyDown = (e) => {
    if (!open && e.key !== "Escape") {
      if (e.key === "ArrowDown" && inputValue.length >= minChars) {
        setOpen(true);
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredSuggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredSuggestions[highlightedIndex]) {
          handleSelect(filteredSuggestions[highlightedIndex]);
        }
        break;
      case "Escape":
        setOpen(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleFocus = () => {
    if (inputValue.length >= minChars && filteredSuggestions.length > 0) {
      setOpen(true);
    }
  };

  const handleClickAway = () => {
    setOpen(false);
    setHighlightedIndex(-1);
  };

  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const listElement = listRef.current;
      const highlightedElement = listElement.children[highlightedIndex];
      if (highlightedElement) {
        highlightedElement.scrollIntoView({
          block: "nearest",
          behavior: "smooth",
        });
      }
    }
  }, [highlightedIndex]);

  const showDropdown = open && inputValue.length >= minChars;

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box sx={{ position: "relative", width: fullWidth ? "100%" : "auto" }}>
        <TextField
          inputRef={inputRef}
          label={label}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          disabled={disabled}
          fullWidth={fullWidth}
          size={size}
          variant="outlined"
          autoComplete="off"
          InputLabelProps={{
            shrink: true,
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              backgroundColor: disabled ? "#f1f5f9" : "#ffffff",
              transition: "all 0.2s ease",
              "& fieldset": {
                borderColor: error ? "#dc2626" : "#e2e8f0",
                borderWidth: error ? "2px" : "1.5px",
              },
              "&:hover fieldset": {
                borderColor: error ? "#dc2626" : "#94a3b8",
              },
              "&.Mui-focused fieldset": {
                borderColor: error ? "#dc2626" : "#64748b",
                borderWidth: "2px",
                boxShadow: error
                  ? "0 0 0 3px rgba(220, 38, 38, 0.1)"
                  : "0 0 0 3px rgba(100, 116, 139, 0.1)",
              },
            },
            "& .MuiInputLabel-root": {
              fontSize: "0.85rem",
              color: "#64748b",
              fontWeight: 500,
              "&.Mui-focused": { color: "#475569" },
              "&.Mui-error": { color: "#dc2626" },
            },
            "& .MuiInputBase-input": {
              fontSize: "0.875rem",
              fontWeight: 500,
              color: "#1e293b",
              padding: "10px 14px",
              "&::placeholder": {
                color: "#94a3b8",
                fontWeight: 400,
                opacity: 1,
              },
            },
            "& .MuiFormHelperText-root": {
              fontSize: "0.75rem",
              fontWeight: 500,
              marginLeft: 0,
              marginTop: "4px",
              color: error ? "#dc2626" : "#94a3b8",
            },
          }}
          {...props}
        />

        {showDropdown && (
          <Paper
            elevation={4}
            sx={{
              position: "absolute",
              top: "calc(100% + 4px)",
              left: 0,
              right: 0,
              zIndex: 1300,
              maxHeight: 240,
              overflowY: "auto",
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
              boxShadow: "0px 8px 24px rgba(0,0,0,0.12)",
            }}
          >
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  py: 2,
                }}
              >
                <CircularProgress size={16} />
                <Typography variant="caption" color="#64748b">
                  {loadingText}
                </Typography>
              </Box>
            ) : filteredSuggestions.length === 0 ? (
              <Box sx={{ py: 2, textAlign: "center" }}>
                <Typography variant="caption" color="#94a3b8">
                  {noOptionsText}
                </Typography>
              </Box>
            ) : (
              <List ref={listRef} dense disablePadding>
                {filteredSuggestions.map((item, index) => {
                  const displayValue =
                    item[displayKey] || item[filterKey] || item;
                  const isHighlighted = index === highlightedIndex;

                  return (
                    <ListItem
                      key={item[valueKey] || index}
                      button
                      onClick={() => handleSelect(item)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      sx={{
                        py: 1,
                        px: 2,
                        cursor: "pointer",
                        bgcolor: isHighlighted
                          ? alpha("#2469F0", 0.08)
                          : "transparent",
                        "&:hover": {
                          bgcolor: alpha("#2469F0", 0.06),
                        },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "0.85rem",
                              fontWeight: isHighlighted ? 600 : 500,
                              color: isHighlighted ? "#2469F0" : "#1e293b",
                            }}
                          >
                            {displayValue}
                          </Typography>
                        }
                        secondary={
                          item.dose || item.strength || item.description ? (
                            <Typography
                              variant="caption"
                              sx={{ fontSize: "0.7rem", color: "#94a3b8" }}
                            >
                              {item.dose || item.strength || item.description}
                            </Typography>
                          ) : null
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default CustomAutocompleteInput;